import { useState } from 'react';
import {
  servicosApi,
  type AtualizarServicoRequest,
  type ServicoResumo,
} from '@/features/servicos/api/servicosApi';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { InputMonetario } from '@/shared/ui/InputMonetario';

type Modo =
  | { tipo: 'novo' }
  | { tipo: 'editar'; servico: ServicoResumo };

type Props = {
  modo: Modo;
  aoFechar: () => void;
  aoSalvar: (dados: AtualizarServicoRequest) => Promise<void>;
};

export function ServicoFormModal({ modo, aoFechar, aoSalvar }: Props) {
  const editando = modo.tipo === 'editar';
  const [codigo, setCodigo] = useState(editando ? modo.servico.codigo : '');
  const [nome, setNome] = useState(editando ? modo.servico.nome : '');
  const [preco, setPreco] = useState(editando ? modo.servico.precoUnitario : 0);
  const [ativo, setAtivo] = useState(editando ? modo.servico.ativo : true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      if (editando) {
        await aoSalvar({ nome, precoUnitario: preco, ativo });
      } else {
        await servicosApi.criar({ codigo: codigo.trim() || null, nome, precoUnitario: preco });
        await aoSalvar({ nome, precoUnitario: preco, ativo });
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={editando ? 'Editar serviço' : 'Novo serviço'}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={handleSubmit}>
        {erro && <div className="form-error">{erro}</div>}

        <Field
          rotulo="Código"
          dica={
            editando
              ? 'Código não pode ser alterado.'
              : 'Opcional. Se deixar vazio, será gerado no padrão SRV-NNNN.'
          }
        >
          {(id) => (
            <input
              id={id}
              className="input"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={editando}
              maxLength={50}
            />
          )}
        </Field>

        <Field rotulo="Nome" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              minLength={2}
              maxLength={200}
            />
          )}
        </Field>

        <Field rotulo="Preço unitário" obrigatorio>
          {(id) => (
            <InputMonetario id={id} valor={preco} onChange={setPreco} required />
          )}
        </Field>

        {editando && (
          <Field rotulo="Status">
            {(id) => (
              <label htmlFor={id} className="checkbox">
                <input
                  id={id}
                  type="checkbox"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                />
                <span>Ativo</span>
              </label>
            )}
          </Field>
        )}
      </form>
    </Modal>
  );
}
